/**
 * AttentionCard (KAN-628)
 *
 * Dashboard block "Требуют внимания": clients that need a human (handed to a
 * manager, flagged by the bot, decision-maker found, manual mode, client waits
 * for a reply, client reached the decision-maker (KAN-716), client is interested, client replied but the stage is still the
 * entry one). Clicking a row opens the
 * client's dialog; "Отработано" hides the row until the next client event.
 * Data: GET /api/v1/dashboard/attention/, POST .../attention/{client_id}/done/.
 * KAN-705: when total > items, "Показаны N из M" + "Показать все" (limit=200).
 * KAN-706: tabs "Telegram (N)" / "Звонки (M)" from `totals`, request with
 * `channel`; the header badge is the sum of both channels.
 * KAN-710: a row opens the dialog through ?dialog=<clientId>&estimate=<id>
 * (TranscriptDialogHost in DashboardLayout renders it).
 * KAN-726: "Успех" / "Отказ" next to "Отработано" set the outcome (won / lost
 * stage) and hide the row the same way (POST .../attention/{client_id}/outcome/).
 * KAN-734: view tabs "Требуют внимания" / "Отработанные". The worked view
 * (GET .../attention/?view=worked) lists clients marked "Отработано" without an
 * outcome yet: "отработан <дата>" and only "Успех" / "Отказ". The red badge
 * always counts the clients that need attention.
 * KAN-750: "Отработано" opens a dialog with the manager picker (current
 * manager by default); confirming sends the chosen manager_id with the close.
 * KAN-753: with a session manager chosen, "Отработано" closes at once with
 * that manager (useAttentionDone).
 * KAN-852: "Отработано" became taking the client to work: a manager presses
 * "Взять в работу" (self, at once), owner, operator and senior manager
 * "Назначить в работу" (the manager dialog, the session manager is only the
 * default for a client without a manager). The worked view is "В работе".
 * KAN-903: "Успех" opens the order compose of the client ("Пропустить" there).
 * KAN-794: a muted line under the header when the backend reports open
 * clients without a next step (`no_next_step` > 0).
 * KAN-918: "В работе" rows with new_message_at get "Новое сообщение" and go first.
 * KAN-926: "Требуют внимания" is split into sections (attention_section from the backend).
 * KAN-960: rows of "Требуют внимания" have "Следующий контакт" (AttentionRowActions).
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { BellRing, Loader2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import type { AttentionChannel, AttentionItem, AttentionTotals } from '@/services/api/dashboard'
import { ATTENTION_QUERY_KEY } from '@/services/api/queryKeys'
import { useDialogParam } from '@/hooks/useDialogParam'
import { useOrgTimezone } from '@/hooks/useOrgTimezone'
import { useAttentionChannel } from './useAttentionChannel'
import { attentionListRemover, useAttentionActions } from './useAttentionActions'
import { useAttentionDone } from './useAttentionDone'
import { useAttentionSuccess } from './useAttentionSuccess'
import { AttentionSuccessFlow } from './AttentionSuccessDialog'
import { useAttentionList } from './useAttentionList'
import { AttentionRow } from './AttentionRow'
import { attentionItemTitle, attentionNextContactTarget, noNextStepLine } from './attentionFormat'
import { AttentionRowActions } from './AttentionRowActions'
import { AttentionDoneDialog } from './AttentionDoneDialog'
import type { AttentionAction } from './useAttentionActions'
import { hasNewMessage } from '@/lib/newMessage'
import { ATTENTION_VIEWS, ATTENTION_VIEW_TABS, orderAttentionItems, type AttentionView } from './attentionViews'
import { PillTabs } from './PillTabs'
import { AttentionSections } from '@/components/attention/AttentionSections'

export { ATTENTION_QUERY_KEY }

const CHANNEL_LABELS: Record<AttentionChannel, string> = {
  telegram: 'Telegram',
  call: 'Звонки',
}

function channelTabs(totals: AttentionTotals | undefined) {
  return (Object.keys(CHANNEL_LABELS) as AttentionChannel[]).map((value) => ({
    value,
    label: `${CHANNEL_LABELS[value]} (${totals?.[value] ?? 0})`,
  }))
}

export function AttentionCard() {
  const { openDialog } = useDialogParam()
  const openItem = (item: AttentionItem) =>
    openDialog({
      clientId: item.client_id,
      estimateId: item.estimate_id,
      hint: { title: attentionItemTitle(item) },
    })
  const { channel, setChannel, autoPick } = useAttentionChannel()
  const [view, setView] = useState<AttentionView>('attention')
  // KAN-705: "Показать все" keeps showing all; KAN-706: per tab, reset on switch.
  const [showAll, setShowAll] = useState(false)
  const isWorked = view === 'worked'

  // The attention list always loads: it feeds the red badge in both views.
  const attention = useAttentionList('attention', channel, showAll && !isWorked)
  const worked = useAttentionList('worked', channel, showAll && isWorked, isWorked)
  const active = isWorked ? worked : attention
  const config = ATTENTION_VIEWS[view]

  const attentionTotals = attention.data?.totals
  useEffect(() => {
    autoPick(attentionTotals)
  }, [attentionTotals, autoPick])

  const switchChannel = (next: AttentionChannel) => {
    if (next === channel) return
    setShowAll(false)
    setChannel(next)
  }

  const switchView = (next: AttentionView) => {
    if (next === view) return
    setShowAll(false)
    setView(next)
  }

  const actions = useAttentionActions(active.queryKey, attentionListRemover(channel))
  // KAN-852: "Взять в работу" (manager) or "Назначить в работу" with the manager dialog.
  const done = useAttentionDone(actions.run)
  // KAN-903: "Успех" opens the order compose of the client (useAttentionSuccess).
  const success = useAttentionSuccess(actions.run)
  // KAN-840: срок задачи менеджеру по времени организации.
  const timeZone = useOrgTimezone()
  const onRowAction = (item: AttentionItem, action: AttentionAction) => {
    if (action === 'done') done.request(item)
    else if (action === 'success') success.request(item)
    else actions.run(item.client_id, action)
  }

  const renderRow = (item: AttentionItem) => (
    <AttentionRow
      key={item.client_id}
      item={item}
      onOpen={openItem}
      meta={config.formatMeta(item)}
      metaTestId={config.metaTestId}
      timeZone={timeZone}
      newMessage={config.newMessages && hasNewMessage(item)}
      actions={
        // KAN-828: выбор менеджера (по флагу вида) и кнопки закрытия.
        <AttentionRowActions
          item={item}
          actions={config.actions(item)}
          assignManager={config.assignManager}
          closing={actions.isPending(item.client_id) || success.isPending(item.client_id)}
          onAction={(action) => onRowAction(item, action)}
          doneLabel={done.label}
          nextContact={config.nextContact ? attentionNextContactTarget(item) : undefined}
        />
      }
    />
  )

  const { data, isLoading, error, isFetching } = active
  // KAN-918: in "В работе" rows with a new client message go first.
  const items = orderAttentionItems(config, data?.items ?? [])
  const total = data?.total ?? 0
  // KAN-706: the header badge counts both channels, not only the open tab.
  const badgeCount = attentionTotals
    ? attentionTotals.telegram + attentionTotals.call
    : attention.data?.total ?? 0
  // KAN-794: from the attention list, so the line stays in the worked view too.
  const noNextStep = noNextStepLine(attention.data)

  return (
    <Card data-testid="attention-card">
      <CardHeader className="space-y-3">
        <CardTitle className="flex items-center gap-2">
          Требуют внимания
          {badgeCount > 0 && (
            <span
              data-testid="attention-count"
              className="rounded-full bg-rose-600 px-2 py-0.5 text-xs font-semibold text-white"
            >
              {badgeCount}
            </span>
          )}
        </CardTitle>
        {noNextStep && (
          <p data-testid="attention-no-next-step" className="text-xs text-gray-500">
            {noNextStep}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <PillTabs
            label="Вид"
            tabs={ATTENTION_VIEW_TABS}
            value={view}
            onChange={switchView}
            testIdPrefix="attention-view"
          />
          <span aria-hidden="true" className="hidden h-5 w-px bg-gray-200 sm:block" />
          <PillTabs
            label="Канал"
            tabs={channelTabs(data?.totals)}
            value={channel}
            onChange={switchChannel}
            testIdPrefix="attention-tab"
          />
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : error ? (
          <p className="py-6 text-center text-sm text-red-600">Ошибка загрузки данных</p>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-6 text-gray-400">
            <BellRing className="mb-2 h-6 w-6" />
            <p className="text-sm">{config.emptyText}</p>
          </div>
        ) : (
          <>
            {config.sections ? (
              <AttentionSections
                items={items}
                sectionOf={(item) => item.attention_section}
                renderItems={(group) => <ul>{group.map(renderRow)}</ul>}
                counts={data?.section_counts}
                onShowMore={showAll ? undefined : () => setShowAll(true)}
                showMoreDisabled={isFetching}
              />
            ) : (
              <ul>{items.map(renderRow)}</ul>
            )}
            {(total > items.length || config.clientsLink) && (
              <div
                data-testid="attention-more"
                className="flex flex-wrap items-center justify-between gap-2 border-t px-2 pt-3 text-sm text-gray-500"
              >
                {total > items.length && (
                  <span>
                    Показаны {items.length} из {total}
                  </span>
                )}
                <div className="ml-auto flex items-center gap-2">
                  {config.clientsLink && (
                    <Link to={config.clientsLink.to} className="text-blue-600 hover:underline">
                      {config.clientsLink.label}
                    </Link>
                  )}
                  {total > items.length && !showAll && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={isFetching}
                      onClick={() => setShowAll(true)}
                    >
                      {isFetching && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                      Показать все
                    </Button>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
      <AttentionDoneDialog
        item={done.dialogItem}
        defaultManager={done.defaultManager}
        onConfirm={done.confirm}
        onCancel={done.cancel}
      />
      <AttentionSuccessFlow success={success} />
    </Card>
  )
}
